import type { TGraphQLProviders } from 'entities/provider'
import type { SparklineIntervalInput, TimeRange } from 'generated/graphql'
import { SparklineIntervalTimeUnitEnum, SparklineIntervalTypeEnum } from 'generated/graphql'
import { FeatureFlagsStore } from 'stores'
import { humanizeLargeNumber } from 'utils'
import getUploadDateByValue from 'utils/getUploadDateByValue'
import { getRoute } from 'utils/routes'
import { PATHNAMES } from 'utils/routes/consts'

import { BRAND_CONTENT_TYPE_MAP, TIME_UNIT_MAP } from './brands.consts'
import type { TBrandContentType } from './brands.types'

export const getTimeUnitInterval = (uploadDateParam: string | null): 'day' | 'week' | 'quarter' | 'month' => {
  if (!uploadDateParam || uploadDateParam === 'today' || uploadDateParam === '24h') {
    return 'day'
  }

  return TIME_UNIT_MAP[uploadDateParam] || 'week'
}

export const formatVariablesForQuery = (
  isCustomDate: boolean,
  uploadDateParam: string | null,
  dateValue: TimeRange | null,
  providers: TGraphQLProviders,
  timeUnit: string,
  language: string
) => {
  const convertTimeUnit = (unit: string): SparklineIntervalTimeUnitEnum => {
    switch (unit) {
      case 'day':
        return SparklineIntervalTimeUnitEnum.Day
      case 'week':
        return SparklineIntervalTimeUnitEnum.Week
      case 'month':
        return SparklineIntervalTimeUnitEnum.Month
      case 'quarter':
        return SparklineIntervalTimeUnitEnum.Quarter
      default:
        return SparklineIntervalTimeUnitEnum.Week
    }
  }

  return {
    ...(isCustomDate
      ? { publishedAt: getUploadDateByValue(uploadDateParam), timeRange: null }
      : { timeRange: dateValue }),
    providers,
    interval: {
      type: SparklineIntervalTypeEnum.Calendar,
      timeUnit: convertTimeUnit(timeUnit),
    } as SparklineIntervalInput,
    language,
  }
}

export const getBrandOverviewUrl = (territoryId: string, workspaceId: string): string | null => {
  const [, entityId] = territoryId.split('.')
  if (!entityId) return null
  const isNewPageBrandOverviewEnabled = FeatureFlagsStore.isFeatureFlagEnabled('isNewPageBrandOverviewEnabled')
  const pathname = isNewPageBrandOverviewEnabled ? PATHNAMES.WORKSPACE_BRAND_OVERVIEW : PATHNAMES.WORKSPACE_BRAND
  return getRoute(pathname, { panelId: workspaceId, entityId })
}

export const getBrandContentType = (territoryTypename?: string, territoryType?: string): TBrandContentType | null => {
  return BRAND_CONTENT_TYPE_MAP[territoryTypename] ?? BRAND_CONTENT_TYPE_MAP[territoryType] ?? null
}

export const getCountryDisplayName = (countryCode?: string | null, locale?: string): string | null => {
  if (!countryCode) return null
  const resolvedLocale = locale ?? navigator?.language ?? 'en'
  try {
    return new Intl.DisplayNames([resolvedLocale], { type: 'region' }).of(countryCode) ?? countryCode
  } catch {
    return countryCode
  }
}

/**
 * A brand can be shown/hidden only when it has a territory — without one it has no chart series —
 * and the workspace's own brand additionally needs the module opt-in.
 */
export const isBrandToggleable = (
  brand: { territoryId?: string; isMyBrand?: boolean },
  { canToggleOwner }: { canToggleOwner: boolean }
): boolean => Boolean(brand.territoryId) && (!brand.isMyBrand || canToggleOwner)

/** Charts must never end up empty, so the last brand still visible cannot be hidden. */
export const isLastVisibleBrand = (
  brandId: string,
  toggleableBrandIds: string[],
  hiddenBrandIds: string[]
): boolean => {
  const hidden = new Set(hiddenBrandIds)
  const visible = toggleableBrandIds.filter(id => !hidden.has(id))

  return visible.length === 1 && visible[0] === brandId
}

export const transformToDonutEntities = (
  data: number[] | string[],
  labels: string[],
  colors: string[] | Record<string, string>
) => {
  return labels.map((name, index) => {
    const color = Array.isArray(colors) ? colors[index] : colors[name]
    return {
      id: `${name}-${color}`,
      name,
      y: Number(data[index] ?? 0),
      x: humanizeLargeNumber(Number(data[index] ?? 0), 2),
      color,
    }
  })
}
